import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";

const usd = (n) => (Math.abs(n || 0) >= 1e6 ? `$${(n / 1e6).toFixed(1)}m` : `$${Math.round(n || 0).toLocaleString()}`);

/** One era, one short page: the number, the story of the period and the players behind it. */
export default function EraCohort() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useSeo({
    title: d ? `${d.era}: ${d.headline} — KickStartKenyaFootball` : "Kenyan football by era",
    description:
      "What Kenyan talent was worth in one period, what changed in the game around it, and every player behind the number.",
  });

  useEffect(() => {
    setErr("");
    api.get(`/era/${slug}`).then((r) => setD(r.data)).catch(() => setErr("We could not find that era."));
  }, [slug]);

  if (err)
    return (
      <div className="bg-black px-5 py-24 text-center" data-testid="era-error">
        <h1 className="display-3">{err}</h1>
        <Link to="/valuations" className="btn-primary mt-8 inline-flex">Back to the valuations</Link>
      </div>
    );
  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="era-loading" />;

  const n = d.numbers;
  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="era-cohort">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">Cohort snapshot · {d.era}</p>
          <h1 className="display-1 mt-5 max-w-4xl">{d.headline}</h1>
          <p className="body-copy mt-6 max-w-3xl">{d.story}</p>
          <p className="mt-6 max-w-3xl border-l-2 border-ochre pl-5 text-sm leading-relaxed text-white"
            data-testid="era-lesson">
            {d.lesson}
          </p>
        </Reveal>

        <div className="mt-12 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="era-numbers">
          {[["Careers in this era", n.players],
            ["Carrying a published value", n.players_valued],
            ["Total published value", usd(n.total_usd)],
            ["Average per valued career", usd(n.average_usd)]].map(([k, v]) => (
            <div key={k} className="bg-[#0b0b0b] p-6">
              <p className="mono text-[0.58rem] uppercase tracking-[0.16em] text-zinc-400">{k}</p>
              <p className="mono mt-3 text-sm text-white">{v}</p>
            </div>
          ))}
        </div>

        <section className="mt-14" data-testid="era-players">
          <h2 className="display-3">The Players Behind The Number</h2>
          <div className="mt-6 overflow-x-auto border border-zinc-900">
            <table className="w-full min-w-[760px] text-left" data-testid="era-players-table">
              <thead>
                {d.players.length ? (
                  <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                    {["Player", "Position", "Club", "League", "Where", "Value"].map((h) => (
                      <th key={h} className="mono px-4 py-3 text-[0.58rem] uppercase tracking-[0.16em] text-zinc-300">{h}</th>
                    ))}
                  </tr>
                ) : null}
              </thead>
              <tbody>
                {d.players.map((p) => (
                  <tr key={p.slug} className="border-b border-zinc-900" data-testid={`era-player-${p.slug}`}>
                    <td className="px-4 py-4">
                      <Link to={`/hall-of-fame/${p.slug}`} className="font-display text-sm uppercase text-white hover:text-ochre">
                        {p.name}
                      </Link>
                    </td>
                    <td className="mono px-4 py-4 text-xs text-zinc-300">{p.position || "—"}</td>
                    <td className="mono px-4 py-4 text-xs text-zinc-300">{p.club || "—"}</td>
                    <td className="mono px-4 py-4 text-xs text-zinc-300">{p.league || "—"}</td>
                    <td className="mono px-4 py-4 text-xs text-zinc-300">{p.region || "—"}</td>
                    <td className="mono px-4 py-4 text-xs text-ochre">
                      {p.value_usd || "Not published"}
                      {p.value_estimated ? <span className="ml-2 text-zinc-500">est</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {d.players.length === 0 ? (
            <p className="mt-5 max-w-3xl border-l-2 border-maasai pl-5 text-sm leading-relaxed text-zinc-400"
              data-testid="era-empty">
              {d.empty_note}
            </p>
          ) : null}
          <p className="mt-5 max-w-3xl text-xs leading-relaxed text-zinc-500">{d.note}</p>
        </section>

        <div className="mt-12 flex flex-wrap items-center gap-3">
          {d.previous ? (
            <Link to={`/eras/${d.previous}`} className="btn-ghost inline-flex items-center gap-2" data-testid="era-previous">
              <ArrowLeft size={14} /> The era before
            </Link>
          ) : null}
          {d.next ? (
            <Link to={`/eras/${d.next}`} className="btn-ghost inline-flex items-center gap-2" data-testid="era-next">
              The era after <ArrowRight size={14} />
            </Link>
          ) : null}
          <Link to="/valuations" className="btn-primary inline-flex items-center gap-2" data-testid="era-to-valuations">
            All the valuations <ArrowRight size={14} />
          </Link>
          <Link to={`/hall-of-fame?era=${encodeURIComponent(d.era)}&list=all`} className="btn-ghost"
            data-testid="era-to-archive">
            This era in the archive
          </Link>
        </div>
      </div>
    </div>
  );
}
